import React, { useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Steampunk Parameter Display
 * Shows real-time orb parameters in a vintage mechanical style
 */

const SteampunkDisplay = ({ params = {}, className = '' }) => {
  const [showFormulaPopup, setShowFormulaPopup] = useState(false);
  const {
    energy = 0.5,
    warmth = 0.5,
    complexity = 0.5,
    harmony = 0.5,
    expansion = 0.5
  } = params;

  // Gauge component
  const Gauge = ({ label, value, color, icon }) => {
    const percentage = Math.round(value * 100);
    const rotation = -90 + (value * 180); // -90 to 90 degrees
    
    return (
      <div className="flex flex-col items-center">
        {/* Gauge face */}
        <div 
          className="relative w-16 h-10 overflow-hidden"
          style={{
            background: 'linear-gradient(180deg, #1a1a1a 0%, #0d0d0d 100%)',
            borderRadius: '50px 50px 0 0',
            border: '2px solid #8b7355',
            borderBottom: 'none',
            boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.8), 0 0 10px rgba(139,115,85,0.3)'
          }}
        >
          {/* Tick marks */}
          <div className="absolute inset-0">
            {[0, 25, 50, 75, 100].map((tick, i) => (
              <div
                key={tick}
                className="absolute w-0.5 h-2 bg-amber-600/60"
                style={{
                  left: '50%',
                  bottom: '0',
                  transformOrigin: 'bottom center',
                  transform: `translateX(-50%) rotate(${-90 + tick * 1.8}deg) translateY(-22px)`
                }}
              />
            ))}
          </div>
          
          {/* Needle */}
          <div
            className="absolute bottom-0 left-1/2 w-0.5 h-8 origin-bottom transition-transform duration-500"
            style={{
              transform: `translateX(-50%) rotate(${rotation}deg)`,
              background: `linear-gradient(to top, ${color}, ${color}88)`
            }}
          >
            <div 
              className="absolute -top-1 left-1/2 w-2 h-2 -translate-x-1/2 rounded-full"
              style={{ backgroundColor: color, boxShadow: `0 0 8px ${color}` }}
            />
          </div>
          
          {/* Center pivot */}
          <div 
            className="absolute bottom-0 left-1/2 w-3 h-3 -translate-x-1/2 translate-y-1/2 rounded-full"
            style={{
              background: 'linear-gradient(135deg, #c9a961 0%, #8b7355 100%)',
              boxShadow: '0 0 5px rgba(0,0,0,0.5)'
            }}
          />
        </div>
        
        {/* Label plate */}
        <div 
          className="px-2 py-0.5 -mt-1 text-center"
          style={{
            background: 'linear-gradient(180deg, #2a2a2a 0%, #1a1a1a 100%)',
            border: '1px solid #8b7355',
            borderRadius: '2px',
            boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.5)'
          }}
        >
          <div className="text-amber-500/80 text-[8px] font-mono uppercase tracking-wider">{label}</div>
          <div className="text-amber-300 text-xs font-mono font-bold" style={{ color }}>{percentage}%</div>
        </div>
      </div>
    );
  };

  // Formula display with clickable popup
  const FormulaDisplay = () => (
    <div 
      className="px-3 py-2 text-center cursor-pointer transition-all duration-200 hover:scale-105 group"
      onClick={() => setShowFormulaPopup(true)}
      title="Click to learn about Grant's Formula"
      style={{
        background: 'linear-gradient(180deg, #1a1510 0%, #0d0a05 100%)',
        border: '2px solid #8b7355',
        borderRadius: '4px',
        boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.8), 0 0 15px rgba(139,115,85,0.2)'
      }}
    >
      <div className="text-amber-600/60 text-[8px] uppercase tracking-widest mb-1 group-hover:text-amber-400 transition-colors">
        ✧ Grant Math ✧
      </div>
      <div className="text-amber-400 font-mono text-sm group-hover:text-amber-300 transition-colors" style={{ textShadow: '0 0 10px rgba(255,200,100,0.5)' }}>
        R = α·π/(2·(−θ+3π))
      </div>
      <div className="text-amber-600/40 text-[7px] mt-1 group-hover:text-amber-500/60 transition-colors">
        tap to explore origin
      </div>
    </div>
  );

  // Grant Hicks Formula Origin Popup
  const FormulaPopup = () => (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={() => setShowFormulaPopup(false)}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      
      {/* Popup Content */}
      <div 
        className="relative max-w-lg w-full max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'linear-gradient(135deg, #1a1510 0%, #0d0a05 50%, #1a1510 100%)',
          border: '3px solid #8b7355',
          borderRadius: '12px',
          boxShadow: `
            inset 0 2px 15px rgba(0,0,0,0.8),
            0 0 40px rgba(139,115,85,0.4),
            0 10px 30px rgba(0,0,0,0.6)
          `
        }}
      >
        {/* Brass Header */}
        <div 
          className="text-center py-3 px-4"
          style={{
            background: 'linear-gradient(180deg, #c9a961 0%, #8b7355 50%, #6b5335 100%)',
            borderRadius: '9px 9px 0 0',
            boxShadow: '0 2px 8px rgba(0,0,0,0.5)'
          }}
        >
          <div className="text-sm font-bold tracking-wider" style={{ color: '#1a1510', textShadow: '0 1px 0 rgba(255,255,255,0.3)' }}>
            ⚙ THE GRANT HICKS FORMULA ⚙
          </div>
        </div>
        
        {/* Content */}
        <div className="p-5 space-y-4">
          {/* The Formula */}
          <div 
            className="p-4 text-center rounded-lg"
            style={{
              background: 'linear-gradient(180deg, #1a1510 0%, #0d0a05 100%)',
              border: '2px solid #c9a961',
              boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.8), 0 0 20px rgba(201,169,97,0.2)'
            }}
          >
            <div className="text-amber-400 font-mono text-xl mb-2" style={{ textShadow: '0 0 15px rgba(255,200,100,0.6)' }}>
              R = α·π/(2·(−θ+3π))
            </div>
            <div className="text-amber-600/70 text-xs">The Grantitude Spiral Equation</div>
          </div>
          
          {/* Origin Story */}
          <div className="space-y-3">
            <h3 className="text-amber-400 font-semibold text-sm tracking-wide">📜 ORIGIN</h3>
            <p className="text-amber-200/80 text-sm leading-relaxed">
              This formula emerged from the mind of <span className="text-amber-400 font-semibold">Grant Hicks</span>, 
              a visionary thinker exploring the intersection of mathematics, consciousness, and belonging. 
              While meditating on how spirals appear throughout nature—from galaxies to seashells to the 
              cochlea of the human ear—he discovered this elegant equation.
            </p>
          </div>
          
          {/* The Variables */}
          <div className="space-y-3">
            <h3 className="text-amber-400 font-semibold text-sm tracking-wide">🔮 THE VARIABLES</h3>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="p-2 rounded" style={{ background: 'rgba(139,115,85,0.2)', border: '1px solid #8b7355' }}>
                <span className="text-amber-400 font-mono font-bold">R</span>
                <span className="text-amber-200/70 ml-2">Radial distance from center</span>
              </div>
              <div className="p-2 rounded" style={{ background: 'rgba(139,115,85,0.2)', border: '1px solid #8b7355' }}>
                <span className="text-amber-400 font-mono font-bold">α</span>
                <span className="text-amber-200/70 ml-2">Alpha coefficient (energy)</span>
              </div>
              <div className="p-2 rounded" style={{ background: 'rgba(139,115,85,0.2)', border: '1px solid #8b7355' }}>
                <span className="text-amber-400 font-mono font-bold">θ</span>
                <span className="text-amber-200/70 ml-2">Theta angle (rotation)</span>
              </div>
              <div className="p-2 rounded" style={{ background: 'rgba(139,115,85,0.2)', border: '1px solid #8b7355' }}>
                <span className="text-amber-400 font-mono font-bold">π</span>
                <span className="text-amber-200/70 ml-2">Pi (cosmic constant)</span>
              </div>
            </div>
          </div>
          
          {/* The Philosophy */}
          <div className="space-y-3">
            <h3 className="text-amber-400 font-semibold text-sm tracking-wide">💭 THE PHILOSOPHY</h3>
            <p className="text-amber-200/80 text-sm leading-relaxed">
              Grant believed that <span className="text-amber-400">belonging is not linear</span>—it spirals. 
              We circle back to the same themes in our lives, but each time from a different angle, 
              at a different radius from our center. The formula captures this truth: as θ increases, 
              we spiral either toward connection (expansion) or inward reflection (contraction).
            </p>
            <p className="text-amber-200/60 text-xs italic leading-relaxed mt-2">
              "The denominator approaches zero as we approach 3π—the threshold where mathematics 
              breaks down and belonging begins. This is the asymptote of the soul."
            </p>
            <p className="text-amber-500/60 text-xs text-right">— Grant Hicks</p>
          </div>
          
          {/* The Application */}
          <div className="space-y-3">
            <h3 className="text-amber-400 font-semibold text-sm tracking-wide">⚡ IN M.AI.RY</h3>
            <p className="text-amber-200/80 text-sm leading-relaxed">
              M.ai.ry uses this formula to generate the organic, lava-lamp-like visuals you see 
              in the background. The fractal patterns aren't random—they're mathematical expressions 
              of the conversation's emotional resonance, calculated using Grant's equation.
            </p>
          </div>
        </div>
        
        {/* Close Button */}
        <div className="p-4 border-t border-amber-900/50">
          <button
            onClick={() => setShowFormulaPopup(false)}
            className="w-full py-2 px-4 rounded-lg font-medium text-sm transition-all duration-200 hover:scale-[1.02]"
            style={{
              background: 'linear-gradient(180deg, #c9a961 0%, #8b7355 100%)',
              color: '#1a1510',
              boxShadow: '0 2px 8px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.2)'
            }}
          >
            Close Portal
          </button>
        </div>
        
        {/* Decorative rivets */}
        <div className="absolute top-3 left-3 w-2 h-2 rounded-full" style={{ background: 'linear-gradient(135deg, #c9a961 0%, #6b5335 100%)' }} />
        <div className="absolute top-3 right-3 w-2 h-2 rounded-full" style={{ background: 'linear-gradient(135deg, #c9a961 0%, #6b5335 100%)' }} />
        <div className="absolute bottom-3 left-3 w-2 h-2 rounded-full" style={{ background: 'linear-gradient(135deg, #c9a961 0%, #6b5335 100%)' }} />
        <div className="absolute bottom-3 right-3 w-2 h-2 rounded-full" style={{ background: 'linear-gradient(135deg, #c9a961 0%, #6b5335 100%)' }} />
      </div>
    </div>
  );

  return (
    <div className={`${className}`}>
      {/* Formula Popup - rendered via portal to escape transform context */}
      {showFormulaPopup && createPortal(<FormulaPopup />, document.body)}
      
      {/* Main housing */}
      <div 
        className="p-3 space-y-3"
        style={{
          background: 'linear-gradient(135deg, #1a1510 0%, #0d0a05 50%, #1a1510 100%)',
          border: '3px solid #8b7355',
          borderRadius: '8px',
          boxShadow: `
            inset 0 2px 15px rgba(0,0,0,0.8),
            0 0 20px rgba(139,115,85,0.3),
            0 5px 15px rgba(0,0,0,0.5)
          `
        }}
      >
        {/* Brass plate header */}
        <div 
          className="text-center py-1 px-2 -mx-1 -mt-1"
          style={{
            background: 'linear-gradient(180deg, #c9a961 0%, #8b7355 50%, #6b5335 100%)',
            borderRadius: '4px 4px 0 0',
            boxShadow: '0 2px 5px rgba(0,0,0,0.5)'
          }}
        >
          <div className="text-[10px] font-bold tracking-wider" style={{ color: '#1a1510', textShadow: '0 1px 0 rgba(255,255,255,0.3)' }}>
            ⚙ FRACTAL ENGINE ⚙
          </div>
        </div>

        {/* Formula */}
        <FormulaDisplay />

        {/* Gauges row 1 */}
        <div className="flex justify-center gap-2">
          <Gauge label="Energy" value={energy} color="#ff6b6b" />
          <Gauge label="Warmth" value={warmth} color="#ffa502" />
        </div>

        {/* Gauges row 2 */}
        <div className="flex justify-center gap-2">
          <Gauge label="Complex" value={complexity} color="#a55eea" />
          <Gauge label="Harmony" value={harmony} color="#26de81" />
        </div>

        {/* Expansion bar */}
        <div className="px-1">
          <div className="text-amber-500/60 text-[8px] uppercase tracking-wider text-center mb-1">Expansion</div>
          <div 
            className="h-3 rounded-full overflow-hidden relative"
            style={{
              background: 'linear-gradient(180deg, #0d0a05 0%, #1a1510 100%)',
              border: '1px solid #8b7355',
              boxShadow: 'inset 0 2px 5px rgba(0,0,0,0.8)'
            }}
          >
            <div 
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${expansion * 100}%`,
                background: 'linear-gradient(90deg, #4ecdc4 0%, #44a08d 100%)',
                boxShadow: '0 0 10px rgba(78,205,196,0.5)'
              }}
            />
            {/* Rivets */}
            <div className="absolute top-1/2 left-1 w-1.5 h-1.5 -translate-y-1/2 rounded-full bg-gradient-to-br from-amber-600 to-amber-800" />
            <div className="absolute top-1/2 right-1 w-1.5 h-1.5 -translate-y-1/2 rounded-full bg-gradient-to-br from-amber-600 to-amber-800" />
          </div>
          <div className="text-amber-300 text-[10px] font-mono text-center mt-0.5">{Math.round(expansion * 100)}%</div>
        </div>

        {/* Status lights */}
        <div className="flex justify-center gap-3 pt-1">
          <div className="flex items-center gap-1">
            <div 
              className="w-2 h-2 rounded-full animate-pulse"
              style={{ 
                backgroundColor: energy > 0.7 ? '#ff6b6b' : '#2a2a2a',
                boxShadow: energy > 0.7 ? '0 0 8px #ff6b6b' : 'none'
              }} 
            />
            <span className="text-[8px] text-amber-600/50">HIGH</span>
          </div>
          <div className="flex items-center gap-1">
            <div 
              className="w-2 h-2 rounded-full"
              style={{ 
                backgroundColor: '#26de81',
                boxShadow: '0 0 8px #26de81',
                animation: 'pulse 2s infinite'
              }} 
            />
            <span className="text-[8px] text-amber-600/50">SYNC</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SteampunkDisplay;
